import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, TrendingUp, Lock, CheckCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const MUX_PLAYBACK_ID = "RlMGmYMeUsN4uhwj1t00k2PcGxIBMF01irQMUx9dXkV2c";

const fadeUp = { initial: { opacity: 0, y: 40 }, whileInView: { opacity: 1, y: 0 }, transition: { duration: 0.8 }, viewport: { once: true } };

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

const PRODUCTS = [
  {
    carrier: "Allianz",
    name: "Allianz 222+",
    bonus: "52%",
    bonusNum: 52,
    tagline: "Biggest premium bonus in Allianz history",
    features: [
      "52% PIV premium bonus credited at issue",
      "0% floor — designed to help limit market-driven losses",
      "Index Lock — lock in gains at any point during crediting period",
      "Increasing income that locks in at each new level",
      "Multiple index strategies (S&P 500, Bloomberg, Morgan Stanley)"
    ],
    color: "#003087",
    highlight: "Up to 52% credited to Protected Income Value on day one"
  },
  {
    carrier: "Athene",
    name: "Performance Elite 10 & 15",
    bonus: "34%",
    bonusNum: 34,
    tagline: "Increased premium bonus through June 30",
    features: [
      "Up to 26% (PE 10) or 34% (PE 15) premium bonus",
      "Credited at issue — immediate boost to accumulation value",
      "Growth potential with protection from market downturns",
      "No direct stock market risk",
      "Flexible income options"
    ],
    color: "#7B2D8E",
    highlight: "Premium bonus credited at issue, supporting outcomes from day one"
  },
  {
    carrier: "North American",
    name: "Charter Plus 10 & 14",
    bonus: "29%",
    bonusNum: 29,
    tagline: "Up to 29% with Enhanced Bonus Rider",
    features: [
      "Up to 23% (Plus 10) or 29% (Plus 14) total premium bonus with EBR",
      "Limited-time 4% bonus increase special",
      "AV True-Up feature — designed so charges don't exceed interest credits",
      "20 index account options — 18 uncapped",
      "Indexes from Morgan Stanley, Fidelity, S&P, Goldman Sachs, Barclays"
    ],
    color: "#1A5632",
    highlight: "Includes limited-time 4% premium bonus increase special"
  }
];

const ProductsPage = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', age: '', state: '', premium_amount: '', growth_period: '', product: '', current_situation: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Load Taboola pixel
  useEffect(() => {
    window._tfa = window._tfa || [];
    window._tfa.push({notify: 'event', name: 'page_view', id: 2036933});
    if (!document.getElementById('tb_tfa_script')) {
      const s = document.createElement('script');
      s.async = 1;
      s.src = '//cdn.taboola.com/libtrc/unip/2036933/tfa.js';
      s.id = 'tb_tfa_script';
      const first = document.getElementsByTagName('script')[0];
      first.parentNode.insertBefore(s, first);
    }

    // Google Tag Manager
    if (!document.getElementById('gtm_script')) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({'gtm.start': new Date().getTime(), event: 'gtm.js'});
      const g = document.createElement('script');
      g.async = true;
      g.id = 'gtm_script';
      g.src = 'https://www.googletagmanager.com/gtm.js?id=GTM-PDRR56MH';
      const first = document.getElementsByTagName('script')[0];
      first.parentNode.insertBefore(g, first);
    }

    // Meta (Facebook) Pixel — client-side. Paired with server-side CAPI
    // via a shared event_id sent on the form submit, so FB deduplicates.
    if (!window.fbq) {
      /* eslint-disable */
      !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
      n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      /* eslint-enable */
      window.fbq('init', '2745324649171352');
      window.fbq('track', 'PageView');
    }

    // Mux Player web component (single tag, drops anywhere in JSX)
    if (!document.getElementById('mux-player-script')) {
      const m = document.createElement('script');
      m.id = 'mux-player-script';
      m.src = 'https://cdn.jsdelivr.net/npm/@mux/mux-player';
      m.type = 'module';
      document.head.appendChild(m);
    }
  }, []);

  // Helper: read a browser cookie (used for _fbp / _fbc → CAPI dedup match quality)
  const getCookie = (name) => {
    const m = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return m ? decodeURIComponent(m[2]) : null;
  };

  // Helper: generate a UUID-like event_id for client/server dedup
  const generateEventId = () => {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    return 'evt-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const setField = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Required';
    if (!formData.email.trim()) newErrors.email = 'Required';
    if (!formData.phone.trim()) newErrors.phone = 'Required';
    if (!formData.age.trim()) newErrors.age = 'Required';
    if (!formData.state) newErrors.state = 'Required';
    if (!formData.premium_amount.trim()) newErrors.premium_amount = 'Required';
    if (!formData.growth_period) newErrors.growth_period = 'Required';
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }

    setSubmitting(true);
    try {
      // Generate shared event_id for client + server dedup
      const eventId = generateEventId();
      const eventSourceUrl = window.location.href;
      const fbp = getCookie('_fbp');
      const fbc = getCookie('_fbc');

      await fetch(`${API_URL}/api/leads/product`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          fb_event_id: eventId,
          fb_event_source_url: eventSourceUrl,
          fbp,
          fbc,
        })
      });

      // Fire Meta Pixel Lead event (client-side) with SAME event_id → dedup
      if (window.fbq) {
        window.fbq('track', 'Lead', {
          content_name: 'Premium Bonus Proposal',
          content_category: formData.product || 'Premium Bonus',
        }, { eventID: eventId });
      }

      // Fire Taboola conversion event
      window._tfa = window._tfa || [];
      window._tfa.push({notify: 'event', name: 'lead', id: 2036933});

      // GTM dataLayer conversion event
      if (window.dataLayer) {
        window.dataLayer.push({ event: 'premium_bonus_lead', source: 'premium_bonus_page' });
      }

      setSubmitted(true);
    } catch (err) {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* Google Tag Manager (noscript) */}
      <noscript><iframe src="https://www.googletagmanager.com/ns.html?id=GTM-PDRR56MH" height="0" width="0" style={{display:'none',visibility:'hidden'}} title="gtm"></iframe></noscript>
      {/* Meta Pixel (noscript fallback) */}
      <noscript><img height="1" width="1" style={{display:'none'}} src="https://www.facebook.com/tr?id=2745324649171352&ev=PageView&noscript=1" alt="" /></noscript>
      {/* Hero with Video + Form Above the Fold */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0A192F] via-[#0A192F] to-[#1B4D3E]">
        {/* Subtle gold glow */}
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute top-1/3 -left-32 w-[700px] h-[700px] bg-[#C5A059] rounded-full blur-[200px]" />
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[#1B4D3E] rounded-full blur-[180px]" />
        </div>
        <div id="get-proposal" className="relative z-10 max-w-[1400px] mx-auto px-4 md:px-8 pt-24 md:pt-28 pb-12">

          {/* Top: Slim header copy (full width) */}
          <motion.div
            className="max-w-[1100px] mb-8 md:mb-10"
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
          >
            <div
              className="inline-flex items-center gap-2 bg-[#C5A059] text-[#0A192F] px-3.5 py-1.5 mb-5"
              data-testid="deadline-ribbon"
            >
              <span className="text-[11px] md:text-xs tracking-[0.25em] uppercase font-bold">Special Program &middot; Deadline June 15</span>
            </div>
            <p className="text-[15px] md:text-base text-white/85 leading-[1.5] mb-5 max-w-2xl border-l-2 border-[#C5A059] pl-5">
              Close to retirement with too much in the market? You're starting to realize <span className="text-[#C5A059] font-semibold">&ldquo;ride it out&rdquo;</span> only works if you still have 20 years to recover.
            </p>
            <h1 className="text-[32px] sm:text-[42px] lg:text-[52px] xl:text-[60px] font-light text-white leading-[1.05] tracking-[-0.01em]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Up to <span className="text-[#C5A059]">52%</span> Premium Bonus
              <span className="block text-[#C5A059]">on Select Annuities.</span>
            </h1>
          </motion.div>

          {/* Two-column: video left, form right */}
          <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-8 lg:gap-10 items-start">
            {/* LEFT: Video */}
            <motion.div
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.2 }}
              data-testid="hero-video-wrap"
            >
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-br from-[#C5A059]/40 to-transparent blur-xl" />
                <div className="relative bg-black aspect-video shadow-2xl overflow-hidden">
                  <mux-player
                    playback-id={MUX_PLAYBACK_ID}
                    stream-type="on-demand"
                    primary-color="#C5A059"
                    accent-color="#0A192F"
                    poster={`https://image.mux.com/${MUX_PLAYBACK_ID}/thumbnail.jpg?width=1920&height=1080&fit_mode=smartcrop&time=2`}
                    metadata-video-title="Premium Bonus Walkthrough"
                    metadata-viewer-user-id="ridgemont-bonus-page"
                    ref={(el) => {
                      if (!el || el.__rmHooked) return;
                      el.__rmHooked = true;
                      let firedQuarter = false, firedHalf = false, firedThird = false, firedComplete = false;
                      el.addEventListener('play', () => {
                        if (window.fbq) window.fbq('trackCustom', 'VideoPlay', { content_name: 'Premium Bonus Walkthrough' });
                        if (window.dataLayer) window.dataLayer.push({ event: 'video_play', video_title: 'Premium Bonus Walkthrough' });
                      });
                      el.addEventListener('timeupdate', () => {
                        const d = el.duration; const t = el.currentTime;
                        if (!d || !isFinite(d)) return;
                        const pct = (t / d) * 100;
                        if (!firedQuarter && pct >= 25) {
                          firedQuarter = true;
                          if (window.dataLayer) window.dataLayer.push({ event: 'video_progress', percent: 25 });
                        }
                        if (!firedHalf && pct >= 50) {
                          firedHalf = true;
                          if (window.fbq) window.fbq('track', 'ViewContent', { content_name: 'Premium Bonus Walkthrough', content_category: 'Video 50%' });
                          if (window.dataLayer) window.dataLayer.push({ event: 'video_progress', percent: 50 });
                        }
                        if (!firedThird && pct >= 75) {
                          firedThird = true;
                          if (window.dataLayer) window.dataLayer.push({ event: 'video_progress', percent: 75 });
                        }
                      });
                      el.addEventListener('ended', () => {
                        if (firedComplete) return;
                        firedComplete = true;
                        if (window.fbq) window.fbq('trackCustom', 'VideoComplete', { content_name: 'Premium Bonus Walkthrough' });
                        if (window.dataLayer) window.dataLayer.push({ event: 'video_complete' });
                      });
                    }}
                    style={{ width: '100%', height: '100%', display: 'block', '--media-object-fit': 'cover' }}
                  ></mux-player>
                </div>
              </div>
              {/* Caption row under video */}
              <p className="text-white/60 text-[13px] mt-3 text-center md:text-left">
                <span className="text-[#C5A059] font-semibold">53 seconds</span> with Brandi Jo &mdash; the bonus, the math, the deadline.
              </p>
            </motion.div>

            {/* RIGHT: Form (above the fold) */}
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.4 }}>
              {submitted ? (
                <div className="bg-[#1B4D3E] p-10 text-center shadow-2xl" data-testid="proposal-success">
                  <CheckCircle className="w-16 h-16 text-[#C5A059] mx-auto mb-4" />
                  <h3 className="text-2xl font-light text-white mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Your Proposal Is On the Way!</h3>
                  <p className="text-white/70">We'll send your personalized illustrated proposal within 24 hours.</p>
                </div>
              ) : (
                <div className="bg-white p-7 md:p-8 shadow-2xl" data-testid="proposal-form">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs tracking-[0.2em] uppercase text-[#C5A059] font-semibold">Free Illustrated Proposal</p>
                    <p className="text-[10px] tracking-wider uppercase text-red-600 font-semibold">&middot; Deadline June 15</p>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-light text-[#0A192F] mb-1.5" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    See what your bonus looks like.
                  </h3>
                  <p className="text-sm text-[#57534E] mb-5">Personalized to your savings &amp; timeline. No cost. No obligation.</p>
                  <form onSubmit={handleSubmit} className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Input name="name" placeholder="Full Name *" value={formData.name} onChange={handleChange} className={errors.name ? 'border-red-500' : ''} data-testid="product-name" />
                      </div>
                      <div>
                        <Input name="age" type="number" placeholder="Age *" min="40" max="80" value={formData.age} onChange={handleChange} className={errors.age ? 'border-red-500' : ''} data-testid="product-age" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Input name="email" type="email" placeholder="Email *" value={formData.email} onChange={handleChange} className={errors.email ? 'border-red-500' : ''} data-testid="product-email" />
                      </div>
                      <div>
                        <Input name="phone" type="tel" placeholder="Phone *" value={formData.phone} onChange={handleChange} className={errors.phone ? 'border-red-500' : ''} data-testid="product-phone" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Input name="premium_amount" placeholder="Premium Amount *" value={formData.premium_amount} onChange={handleChange} className={errors.premium_amount ? 'border-red-500' : ''} data-testid="product-premium" />
                      </div>
                      <div>
                        <Select value={formData.state} onValueChange={(val) => setField('state', val)}>
                          <SelectTrigger className={errors.state ? 'border-red-500' : ''} data-testid="product-state">
                            <SelectValue placeholder="State of Residence *" />
                          </SelectTrigger>
                          <SelectContent className="max-h-[260px]">
                            {US_STATES.map((s) => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Select value={formData.growth_period} onValueChange={(val) => setField('growth_period', val)}>
                          <SelectTrigger className={errors.growth_period ? 'border-red-500' : ''} data-testid="product-period">
                            <SelectValue placeholder="Growth Period *" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="10">10 Years</SelectItem>
                            <SelectItem value="15">15 Years</SelectItem>
                            <SelectItem value="20">20 Years</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Select value={formData.product} onValueChange={(val) => setField('product', val)}>
                          <SelectTrigger data-testid="product-select">
                            <SelectValue placeholder="Carrier Interest" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Allianz">Allianz</SelectItem>
                            <SelectItem value="Athene">Athene</SelectItem>
                            <SelectItem value="North American">North American</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <Button type="submit" disabled={submitting} className="w-full bg-[#C5A059] text-[#0A192F] hover:bg-[#C5A059]/90 py-6 text-base font-semibold" data-testid="product-submit">
                      {submitting ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Submitting...</> : <>Get My Illustrated Proposal</>}
                    </Button>
                    <p className="text-[11px] text-[#57534E] text-center mt-1">Your info is secure. We don't share or sell it.</p>
                  </form>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>


      {/* Trust strip — carrier bullets in a thin row directly under the hero */}
      <section className="bg-[#0A192F] border-t border-b border-[#C5A059]/20 py-6 md:py-8" data-testid="carrier-strip">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <p className="text-xs tracking-[0.25em] uppercase text-[#C5A059] font-semibold text-center mb-4">
            The biggest carrier bonuses available right now
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
            {[
              { carrier: "Allianz 222+", detail: "up to 52% PIV bonus credited at issue" },
              { carrier: "Athene Performance Elite", detail: "increased premium bonus through June 30" },
              { carrier: "North American Charter Plus", detail: "limited-time 4% bonus increase" }
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 text-white/85 text-sm md:text-[15px] leading-snug">
                <CheckCircle className="w-5 h-5 text-[#C5A059] flex-shrink-0 mt-0.5" />
                <span><span className="text-white font-medium">{item.carrier}</span> &mdash; {item.detail}</span>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* What Premium Bonuses Mean */}
      <section className="py-24 md:py-32 bg-[#0A192F]">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <motion.div {...fadeUp} className="text-center">
            <p className="text-sm tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-4">What This Means For You</p>
            <h2 className="text-4xl md:text-5xl font-light text-white tracking-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Your Money Gets a
              <span className="block text-[#C5A059]">Head Start from Day One</span>
            </h2>
            <p className="text-lg text-white/70 leading-relaxed mb-16 max-w-2xl mx-auto">
              A premium bonus adds a percentage of your deposit to your annuity value immediately. 
              It's like starting a race 20-52% ahead of the starting line.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { icon: TrendingUp, title: "Immediate Boost", desc: "Bonus is credited the day your contract is issued. Your accumulation value starts higher than what you deposited." },
                { icon: ShieldCheck, title: "Protected Growth", desc: "Your principal and bonus grow with market-linked interest, paired with a 0% floor designed to help limit market-driven losses." },
                { icon: Lock, title: "Locked-In Gains", desc: "Once interest is credited to your contract value, those gains are locked in — subsequent market drops don't reduce credited amounts." }
              ].map((item, i) => (
                <motion.div key={i} className="bg-white/5 border border-white/10 p-8 text-center"
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.15 }} viewport={{ once: true }}>
                  <div className="w-14 h-14 bg-[#C5A059]/10 flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-7 h-7 text-[#C5A059]" />
                  </div>
                  <h4 className="text-white font-medium text-lg mb-3">{item.title}</h4>
                  <p className="text-white/60 text-sm leading-relaxed">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Product Comparison */}
      <section id="products" className="py-24 md:py-32 bg-[#FAFAF9]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <p className="text-sm tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-4">Compare Offers</p>
            <h2 className="text-4xl md:text-5xl font-light text-[#0A192F] tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Three Carriers. One Goal.
            </h2>
            <p className="text-lg text-[#57534E] mt-4">The biggest premium bonuses available right now — all in one place.</p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {PRODUCTS.map((product, i) => (
              <motion.div key={product.carrier} className="bg-white border border-[#E7E5E4] overflow-hidden flex flex-col"
                initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.15 }} viewport={{ once: true }}>
                {/* Header */}
                <div className="p-8 text-center" style={{ background: '#0A192F' }}>
                  <p className="text-xs tracking-[0.2em] uppercase text-white/40 mb-2">{product.carrier}</p>
                  <p className="text-5xl font-light text-[#C5A059] mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {product.bonus}
                  </p>
                  <p className="text-white/60 text-sm">Premium Bonus</p>
                </div>
                {/* Product name */}
                <div className="px-8 pt-6 pb-2">
                  <h3 className="text-lg font-medium text-[#0A192F]">{product.name}</h3>
                  <p className="text-sm text-[#C5A059] mt-1">{product.tagline}</p>
                </div>
                {/* Features */}
                <div className="px-8 py-4 flex-1">
                  <ul className="space-y-3">
                    {product.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#1B4D3E]" />
                        <span className="text-sm text-[#57534E]">{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                {/* Highlight */}
                <div className="px-8 py-4 border-t border-[#E7E5E4] bg-[#FAFAF9]">
                  <p className="text-xs text-[#57534E] italic">{product.highlight}</p>
                </div>
                {/* CTA */}
                <div className="px-8 pb-8 pt-2">
                  <Button onClick={() => { setField('product', product.carrier); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    className="w-full bg-[#C5A059] text-[#0A192F] hover:bg-[#C5A059]/90 py-6 font-semibold"
                    data-testid={`product-cta-${product.carrier.toLowerCase()}`}>
                    Get {product.carrier} Proposal
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner — final scroll-back-up nudge */}
      <section className="py-16 bg-[#C5A059]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 text-center">
          <motion.div {...fadeUp}>
            <h3 className="text-2xl md:text-3xl font-light text-[#0A192F] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              These Bonuses Won't Last Forever
            </h3>
            <p className="text-[#0A192F]/70 mb-8 max-w-xl mx-auto">
              Premium bonus promotions are limited-time offers from each carrier. Get your personalized proposal before rates change.
            </p>
            <Button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="bg-[#0A192F] text-white hover:bg-[#0A192F]/90 px-8 py-6 text-lg" data-testid="cta-back-to-form">
              <ArrowRight className="w-5 h-5 mr-2" />Get My Free Proposal
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 bg-[#0A192F] border-t border-white/10">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <p className="text-white/20 text-xs leading-relaxed md:max-w-4xl">
            Premium bonuses are subject to specific terms and conditions set by each carrier. Bonus amounts, availability, and product features 
            may vary by state and are subject to change without notice. Allianz 222+ PIV bonus requires 10+ contract years and lifetime withdrawals 
            between ages 60-100. North American Charter Plus bonuses include limited-time 4% increase special; Enhanced Bonus Rider costs 0.95% annually. 
            Athene Performance Elite increased bonus available through June 30. Guarantees are backed solely by the financial strength and 
            claims-paying ability of the issuing insurance company. This is not an offer to sell or a solicitation of an offer to buy any security.
          </p>
          <a
            href="https://substack.com/@retirewithcertainty"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#FF6719] hover:text-white text-xs font-semibold whitespace-nowrap transition-colors self-start"
            data-testid="products-substack-link"
          >
            Retire With Certainty &middot; Newsletter &rarr;
          </a>
        </div>
      </section>
    </div>
  );
};

export default ProductsPage;
